import { stringify } from "query-string"

export async function request({ queryKey = null }) {
  const _key = queryKey[0]
  const { path, options, params, data } = queryKey[1]
  const csrfMeta = document.querySelector("meta[name=csrf-token]")
  const csrf = csrfMeta instanceof HTMLMetaElement ? csrfMeta.content : null
  let token = localStorage.getItem("jwt_token")

  if (
    !token ||
    token === "null" ||
    token === "undefined" ||
    token.trim() === ""
  ) {
    // Check URL query parameters for single-use token
    const urlParams = new URLSearchParams(window.location.search)
    const singleUseToken = urlParams.get("sut")
    const bandId = urlParams.get("bn")

    if (singleUseToken && bandId) {
      // we got an SUT - try exchanging it for a JWT
      token = await exchangeSUTforJWT(singleUseToken, bandId)
      localStorage.setItem("jwt_token", token)
    }
  }

  const headers = new Headers()
  headers.set("Content-Type", "application/json")
  headers.set("Accept", "application/json")
  if (csrf) headers.set("X-CSRF-Token", csrf)
  if (token) headers.set("Authorization", `Bearer ${token}`)

  var bodyData = JSON.stringify(data)

  const url = `${path}${
    params ? `?${stringify(params, { arrayFormat: "bracket" })}` : ""
  }`

  const response = await fetch(url, {
    headers: headers,
    credentials: "same-origin",
    body: bodyData,
    ...options,
  })
  if (response.status === 500) {
    throw new Error(await tryParseJSON(response))
  }
  if (response.status === 404) {
    throw new Error(`404 not found`)
  }
  if (response.status === 401) {
    // our token died - kick out to the login page
    window.location.href = "/"
    return
  }
  return response.json()
}

export async function mutationRequest(query) {
  return request({ queryKey: ["mutation", query] })
}

/**
 * Exchanges a single-use token for a JWT by making a request to the API
 * @param {string} singleUseToken - The single-use token from URL
 * @param {string} id - The band ID from URL
 * @returns {Promise<string>} - The JWT token
 */
async function exchangeSUTforJWT(singleUseToken, id) {
  const csrfMeta = document.querySelector("meta[name=csrf-token]")
  const csrf = csrfMeta instanceof HTMLMetaElement ? csrfMeta.content : null

  const headers = new Headers()
  headers.set("Content-Type", "application/json")
  headers.set("Accept", "application/json")
  if (csrf) headers.set("X-CSRF-Token", csrf)

  const response = await fetch("/api/v1/users", {
    method: "POST",
    headers: headers,
    credentials: "same-origin",
    body: JSON.stringify({
      flow: "single_use_token",
      referral_token: localStorage.getItem("referral_token"),
      user: {
        single_use_token: singleUseToken,
        id: id,
      },
    }),
  })

  if (!response.ok) {
    console.error("Failed to exchange single-use token for JWT")
    return null
  }

  const data = await response.json()
  return data.token
}

/**
 * Fetches a file with auth headers and returns a Blob.
 * Use this for CSV/file download endpoints.
 */
export async function downloadRequest(path) {
  const token = localStorage.getItem("jwt_token")
  const csrfMeta = document.querySelector("meta[name=csrf-token]")
  const csrf = csrfMeta instanceof HTMLMetaElement ? csrfMeta.content : null

  const headers = new Headers()
  if (csrf) headers.set("X-CSRF-Token", csrf)
  if (token) headers.set("Authorization", `Bearer ${token}`)

  const response = await fetch(path, { headers, credentials: "same-origin" })
  if (!response.ok) throw new Error(`Request failed: ${response.status}`)
  return response.blob()
}

// parse response as JSON if it is - or return string for error handling
/** @param {Response} response */
async function tryParseJSON(response) {
  const contentType = response.headers.get("content-type")

  try {
    if (contentType && contentType.includes("application/json")) {
      const json = await response.json()
      return JSON.stringify(json, null, 2)
    } else if (contentType && contentType.includes("text/html")) {
      return `An unknown error occured:  ${response.statusText} (${response.status})`
    } else {
      return await response.text()
    }
  } catch (error) {
    return `${response.statusText} (${response.status})`
  }
}
